// 文件名: src/App.js
import React, { useState, useEffect } from 'react';
import { Target, ShieldCheck, User, Globe, LayoutTemplate, ShieldAlert, LogIn, Loader } from 'lucide-react';
import TD1501Calculator from './components/TD1501';
import Td2000g3Bg3Calculator from './components/Td2000g3Bg3.js';
import Td2000g2Bg2Calculator from './components/Td2000g2Bg2.js';
import TD1502Calculator from './components/TD1502.js';
import PlanDesigner from './components/PlanDesigner';
import { useTranslation } from './i18n';
import { UnitProvider, useUnit } from './contexts/UnitContext';
import { Ruler } from 'lucide-react';

const AppView = ({ isAdmin }) => {
    const [activeTab, setActiveTab] = useState('TD2000G3/BG3');
    const [lastCalculatorTab, setLastCalculatorTab] = useState('TD2000G3/BG3');
    const { t, language, setLanguage } = useTranslation();
    const { unitSystem, toggleUnitSystem } = useUnit();

    const [planDesignerState, setPlanDesignerState] = useState({
        floorPlan: null,
        floorPlanBase64: null, // 底图的 Base64 数据，用于导出设计时避免 fetch 失效的 Blob URL
        floorPlanNaturalSize: null, // { width, height } - 底图原始尺寸，用于浏览器缩放时坐标转换
        mode: 'idle',
        scalePoints: [],
        scale: null,
        showScaleModal: false,
        scaleInput: 5000,
        measurePoints: [],
        measurements: [],
        selectedMeasurementId: null,
        cameras: [],
        showCameraModal: false,
        newCameraPosition: null,
        selectedCamera: null,
        isDragging: false,
        draggedCameraId: null,
        dragOffset: { x: 0, y: 0 },
        walls: [],
        selectedWallId: null,
        areWallsVisible: true,
        floorPlanOpacity: 1,
        textBoxes: [],
        selectedTextBoxId: null,
        isDraggingTextBox: false,
        draggedTextBoxId: null,
    });

    const TABS = {
        'TD2000G3/BG3': <Td2000g3Bg3Calculator isAdmin={isAdmin} />,
        'TD2000G2/BG2': <Td2000g2Bg2Calculator isAdmin={isAdmin} />,
        'TD1501': <TD1501Calculator isAdmin={isAdmin} />,
        'TD1502': <TD1502Calculator isAdmin={isAdmin} />,
    };

    const handleTabClick = (tabName) => {
        setActiveTab(tabName);
        if (TABS[tabName]) {
            setLastCalculatorTab(tabName);
        }
    };

    const handlePlanDesignerBack = () => {
        setActiveTab(lastCalculatorTab);
    };

    const toggleLanguage = () => {
        setLanguage(prev => prev === 'en' ? 'zh' : 'en');
    };

    const Header = () => (
        <header className="bg-gray-800 text-white p-4 shadow-md">
            <div className="container mx-auto flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Target className="w-10 h-10 text-cyan-400" />
                    <div>
                        <h1 className="text-2xl font-bold">{t('appTitle')}</h1>
                        <p className="text-sm text-gray-400">{t('appSubtitle')}</p>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <button onClick={toggleLanguage} className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-700 hover:bg-gray-600 rounded-full transition-colors">
                        <Globe className="w-5 h-5" />
                        <span>{language === 'en' ? '中文' : 'en'}</span>
                    </button>
                    <button onClick={toggleUnitSystem} className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-700 hover:bg-gray-600 rounded-full transition-colors">
                        <Ruler className="w-5 h-5" />
                        <span className="font-semibold px-1">{unitSystem === 'metric' ? 'm' : 'ft'}</span>
                    </button>

                    <div className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-full ${isAdmin ? 'bg-green-500/20 text-green-300' : 'bg-sky-500/20 text-sky-300'}`}>
                        {isAdmin ? <ShieldCheck className="w-5 h-5" /> : <User className="w-5 h-5" />}
                        <span>{isAdmin ? t('adminMode') : t('visitorMode')}</span>
                    </div>
                </div>
            </div>
        </header>
    );

    return (
        <>
            <Header />
            <main className="container mx-auto p-4 md:p-8 flex flex-col flex-grow">
                <div className="mb-8">
                    <div className="border-b border-gray-200">
                        <div className="flex justify-between items-center overflow-x-auto">
                            <nav className="-mb-px flex space-x-6" aria-label="Tabs">
                                {Object.keys(TABS).map(tabName => (
                                    <button
                                        key={tabName}
                                        onClick={() => handleTabClick(tabName)}
                                        className={`${activeTab === tabName
                                            ? 'border-cyan-500 text-cyan-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-lg transition-colors`}
                                    >
                                        {tabName}
                                    </button>
                                ))}
                            </nav>
                            {!isAdmin && (
                                <button
                                    onClick={() => handleTabClick('planDesigner')}
                                    className={`ml-auto flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ease-in-out transform focus:outline-none whitespace-nowrap ${activeTab === 'planDesigner'
                                        ? 'bg-cyan-600 text-white shadow-lg ring-2 ring-offset-2 ring-cyan-400'
                                        : 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-md hover:scale-105 hover:shadow-lg'
                                        }`}
                                >
                                    <LayoutTemplate className="w-4 h-4" />
                                    <span>{t('planDesignerButton')}</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex-grow">
                    {Object.keys(TABS).map(tabName => (
                        <div key={tabName} className={activeTab === tabName ? '' : 'hidden'}>
                            {TABS[tabName]}
                        </div>
                    ))}
                    <div className={activeTab === 'planDesigner' ? '' : 'hidden'}>
                        <PlanDesigner
                            onBack={handlePlanDesignerBack}
                            planState={planDesignerState}
                            setPlanState={setPlanDesignerState}
                        />
                    </div>
                </div>
            </main>
        </>
    );
};

const AccessDenied = () => {
    const { t } = useTranslation();
    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100">
            <div className="text-center p-8 bg-white rounded-lg shadow-xl max-w-md mx-auto">
                <ShieldAlert className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h1 className="text-3xl font-bold text-red-600 mb-4">{t('accessDeniedTitle')}</h1>
                <p className="text-gray-700 mb-6">
                    {t('accessDeniedMessage')}
                </p>
                <a
                    href="https://tdintelligence.wiki/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-cyan-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-cyan-700 transition-colors"
                >
                    <LogIn className="w-5 h-5" />
                    {t('loginButton')}
                </a>
                <p className="text-sm text-gray-500 mt-4">
                    {t('loginInstruction')}
                </p>
            </div>
        </div>
    );
};

export default function App() {
    const [isAdmin, setIsAdmin] = useState(window.location.hash === '#admin');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const { t } = useTranslation();

    useEffect(() => {
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            console.log("Development environment detected. Bypassing SSO authentication.");
            setIsAuthenticated(true);
            setIsLoading(false);
            return;
        }

        const checkAuthCookie = () => {
            const cookies = document.cookie.split(';');
            for (let i = 0; i < cookies.length; i++) {
                let cookie = cookies[i].trim();
                if (cookie.startsWith('auth=')) {
                    const value = cookie.substring('auth='.length);
                    if (value.length > 0) {
                        return true;
                    }
                }
            }
            return false;
        };

        if (checkAuthCookie()) {
            console.log("Local auth cookie found. Authentication successful.");
            setIsAuthenticated(true);
            setIsLoading(false);
        } else {
            console.log("No local auth cookie. Attempting SSO via iframes.");

            const ssoProviders = [
                {
                    origin: 'https://emea.tdintelligence.wiki',
                    authPagePath: '/Calculator-Auth-240c089896d88023a7e2f0f6ded91c79'
                },
                {
                    origin: 'https://shoppertrak.tdintelligence.wiki',
                    authPagePath: '/Calculator-Auth-240c089896d88023a7e2f0f6ded91c79'
                },
                {
                    origin: 'https://america.tdintelligence.wiki',
                    authPagePath: '/Calculator-Auth-240c089896d88023a7e2f0f6ded91c79'
                },
                {
                    origin: 'https://asia.tdintelligence.wiki',
                    authPagePath: '/Calculator-Auth-240c089896d88023a7e2f0f6ded91c79'
                }
            ];

            let iframes = [];
            let ssoTimeout;

            const cleanup = () => {
                window.removeEventListener('message', handleSsoMessage);
                iframes.forEach(iframe => {
                    if (iframe && document.body.contains(iframe)) {
                        document.body.removeChild(iframe);
                    }
                });
                iframes = [];
                clearTimeout(ssoTimeout);
            };

            const handleSsoMessage = (event) => {
                const isTrustedOrigin = ssoProviders.some(provider => provider.origin === event.origin);
                if (!isTrustedOrigin) {
                    return;
                }

                if (event.data && event.data.type === 'auth-token' && event.data.token) {
                    console.log(`SSO token received from ${event.origin}. Setting local cookie.`);
                    const token = event.data.token;
                    document.cookie = `auth=${token}; path=/; secure`;
                    setIsAuthenticated(true);
                    setIsLoading(false);
                    cleanup();
                }
            };

            const handleTimeout = () => {
                console.log("SSO attempt timed out for all providers.");
                setIsAuthenticated(false);
                setIsLoading(false);
                cleanup();
            };

            ssoProviders.forEach(provider => {
                const ssoProviderUrl = `${provider.origin}${provider.authPagePath}?sso_auth_request=1`;
                let iframe = document.createElement('iframe');
                iframe.style.display = 'none';
                iframe.src = ssoProviderUrl;
                document.body.appendChild(iframe);
                iframes.push(iframe);
            });

            ssoTimeout = setTimeout(handleTimeout, 5000);
            window.addEventListener('message', handleSsoMessage);
        }

        const handleHashChange = () => {
            setIsAdmin(window.location.hash === '#admin');
        };

        window.addEventListener('hashchange', handleHashChange);
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, []);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100">
                <div className="text-center">
                    <Loader className="w-12 h-12 text-cyan-600 animate-spin mx-auto mb-4" />
                    <p className="text-lg text-gray-600">{t('verifyingAccess')}</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <AccessDenied />;
    }

    return (
        <UnitProvider>
            <div className="min-h-screen bg-gray-100 font-sans flex flex-col">
                <AppView isAdmin={isAdmin} />
            </div>
        </UnitProvider>
    );
}
